import { ApolloError } from '@apollo/client';
import { PlacementWithLogical } from '@chakra-ui/react';
import { ColumnFilterPopover, DataTable } from '@gamma/ui/data-table';
import { FlushPanelContent, Panel } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Accessor, Column } from 'react-table';
import { graphqlErrorRedirects, ROUTES } from '@investigator/shared/constants';
import {
  useDateRangeQueryString,
  useIsNewSearch,
  useManualPagination,
  useUserSession,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { OrgTenantsContext } from '@investigator/shared/providers';
import { escapeSpecialChars } from '@investigator/shared/utils/escapeSpecialChars';
import { SeverityScore } from '@investigator/shared/components';
import { useGetTopDetectionsRawPaginated } from '../../queries';

import { AlertCategoriesDetectionsTablePopper } from './AlertCategoriesDetectionsTablePopper';
import { DetectionSummary } from '@investigator/shared/generated/graphql';

export type TopAlertsTableFilters = { id: string; value: string }[];

interface AlertCategoriesDetectionsTableProps {
  hiddenCols?: Accessor<DetectionSummary>[];
  rowPopperPlacement?: PlacementWithLogical;
  onDataLoad?: (data: {
    categories: { severity: number; name: string; detectionCount: number }[];
    totalItems: number;
  }) => void;
}

const dataTableColumns: Column<DetectionSummary>[] = [
  {
    width: 100,
    Header: i18n.tables.score,
    accessor: 'severity',
    Cell: (props) => {
      return <SeverityScore score={props.value ?? 0} />;
    },
  },
  {
    Header: i18n.tables.categories,
    accessor: 'alert_name',
    disableSortBy: true,
    Filter: ColumnFilterPopover,
  },
  {
    width: 135,
    Header: i18n.tables.detectionsHeading,
    accessor: 'total_detections',
  },
];

const mustCondition = (
  filters: { id: string; value: string }[],
  orgTenantsQueryParam?: string[],
) => {
  const formattedQuery = escapeSpecialChars({
    chars: filters?.[0]?.value,
  });

  return [
    `{"regexp":{"alert_info.alert_name.keyword":{"value":".*${formattedQuery}.*","case_insensitive":true}}}`,
    `{"terms": {"tenant": ${JSON.stringify(orgTenantsQueryParam)}}}`,
    '{"term": {"detection_status": "open"}}',
  ];
};

export const AlertCategoriesDetectionsTableFooter = ({
  loading,
  error,
  data,
  signOut,
}: {
  loading: boolean;
  error: ApolloError | undefined;
  data: DetectionSummary[];
  signOut: () => Promise<void>;
}) => {
  if (loading || error) {
    return (
      <Panel>
        <GraphQLReqStatus
          loading={loading}
          error={error}
          signOut={signOut}
          extended={graphqlErrorRedirects}
        />
      </Panel>
    );
  }
  if (!data.length) {
    return <Panel>{i18n.pages.alerts.na}</Panel>;
  } else {
    return null;
  }
};

export const AlertCategoriesDetectionsTable = ({
  hiddenCols = [],
  rowPopperPlacement,
  onDataLoad,
}: AlertCategoriesDetectionsTableProps) => {
  const { signOut } = useUserSession();
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);

  const navigate = useNavigate();

  const { start, end } = useDateRangeQueryString();
  const [initialFetch, setInitialFetch] = useState<boolean>(false);
  const [alertCategoriesTableData, setAlertCategoriesTableData] = useState<
    DetectionSummary[]
  >([]);
  const [filters, setFilters] = useState<
    { id: string; value: string }[] | null
  >(null);

  const {
    sortBy,
    offset,
    pageSize,
    pageIndex,
    pageCount,
    onFetchData,
    getPageCount,
    getSortsForAPI,
  } = useManualPagination<DetectionSummary>({
    initialFetch,
    sortBy: [{ id: 'severity', desc: true }],
  });

  useEffect(() => {
    setInitialFetch(true);
  }, [start, end]);

  const filteredDataTableColumns = useMemo(
    () =>
      dataTableColumns.filter(
        (col) => !hiddenCols.some((hidden) => hidden === col.accessor) && col,
      ),
    [hiddenCols],
  );

  const { isNewSearch } = useIsNewSearch({
    filters,
  });

  const noFilterVariables = {
    start,
    end,
    offset: isNewSearch ? 0 : offset,
    size: pageSize,
    sort: getSortsForAPI(sortBy),
    must_conds: [
      `{"terms": {"tenant": ${JSON.stringify(orgTenantsQueryParam)}}}`,
      '{"term": {"detection_status": "open"}}',
    ],
  };

  const variables = filters?.length
    ? {
        ...noFilterVariables,
        must_conds: mustCondition(filters, orgTenantsQueryParam),
      }
    : noFilterVariables;

  const { data, loading, error, refetch } = useGetTopDetectionsRawPaginated({
    notifyOnNetworkStatusChange: true,
    skip: !start || !end || !orgTenantsQueryParam?.length,
    variables,
  });

  // Handle data updates in useEffect instead of onCompleted
  // to ensure updates happen during proper React lifecycle
  useEffect(() => {
    if (data?.getTopDetectionsRawPaginated) {
      setInitialFetch(false);
      getPageCount(data.getTopDetectionsRawPaginated);
      setAlertCategoriesTableData(data.getTopDetectionsRawPaginated.summaries);
    }
  }, [data, getPageCount]);

  // Notify parent component of data changes
  useEffect(() => {
    if (data?.getTopDetectionsRawPaginated && !loading && onDataLoad) {
      const categories = data.getTopDetectionsRawPaginated.summaries.map(
        (summary) => ({
          severity: summary.severity ?? 0,
          name: summary.alert_name || '',
          detectionCount: summary.total_detections ?? 0,
        }),
      );
      onDataLoad({
        categories,
        totalItems: data.getTopDetectionsRawPaginated.total_items ?? 0,
      });
    }
  }, [data, loading, onDataLoad]);

  const handleColFilter = (filters: TopAlertsTableFilters) => {
    setFilters(filters);
    if (filters.length) {
      refetch({
        ...noFilterVariables,
        offset: 0,
        must_conds: mustCondition(filters, orgTenantsQueryParam),
      });
    }
  };

  const tableData =
    !loading && !error && alertCategoriesTableData.length
      ? alertCategoriesTableData
      : [];

  return (
    <Panel>
      <FlushPanelContent>
        <div data-testid="top-alert-categories-table">
          <DataTable
            isMemod={false}
            hideColumnHeaders={loading}
            isPaginable={Boolean(!loading && !error && tableData.length)}
            isFilterManual={true}
            onColumnFilter={handleColFilter}
            data={tableData}
            pageCount={pageCount}
            autoResetPage={false}
            autoResetSortBy={false}
            isPaginationManual={true}
            columns={filteredDataTableColumns}
            onFetchData={onFetchData}
            rowWrapper={AlertCategoriesDetectionsTablePopper(
              rowPopperPlacement,
            )}
            itemCount={data?.getTopDetectionsRawPaginated?.total_items || 0}
            onRowClick={({ original }) => {
              navigate({
                pathname: ROUTES.detections,
                search: `start=${start}&end=${end}&alert_category=${original.alert_name}`,
              });
            }}
            initialState={{
              pageSize,
              pageIndex,
              sortBy,
            }}
            footer={AlertCategoriesDetectionsTableFooter({
              loading,
              error,
              data: alertCategoriesTableData,
              signOut,
            })}
          />
        </div>
      </FlushPanelContent>
    </Panel>
  );
};
